import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

// Ensure a valid backend base URL even if env isn't injected, and normalize it
const RAW_BASE = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL)
  ? process.env.REACT_APP_BACKEND_URL
  : 'http://localhost:8000';
const BASE = RAW_BASE.replace(/\/+$/, '');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing user session on app start
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token with backend
      fetch(`${BASE}/api/user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Token invalid');
        }
        // Parse JSON from a clone to avoid consuming original body
        const cloned = response.clone();
        return cloned.json();
      })
      .then(data => {
        if (data.success) {
          setUser(data.data.user);
        } else {
          localStorage.removeItem('token');
        }
      })
      .catch(() => {
        localStorage.removeItem('token');
      })
      .finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    // Not needed for localStorage auth - user data is stored locally
    return;
  };

  const login = async (email, password) => {
    console.log('Login attempt with:', { email, password });

    try {
      const response = await fetch(`${BASE}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      let data;
      try {
        // Read JSON from a clone so rrweb can still clone the original
        const cloned = response.clone();
        data = await cloned.json();
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        return {
          success: false,
          error: 'Invalid response from server. Please try again.'
        };
      }

      if (response.ok && data.success) {
        console.log('Login successful for user:', data.data.user.email);
        localStorage.setItem('token', data.data.token);
        setUser(data.data.user);
        return { success: true, data: data.data };
      } else {
        const errorMessage = data.message || (response.status === 401 ? 'Invalid credentials' : 'Login failed');
        console.log('Login failed:', errorMessage);
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: 'Network error. Please check your connection and try again.'
      };
    }
  };

  const register = async (name, email, password) => {
    console.log('Registration attempt with:', { name, email, password });

    try {
      const response = await fetch(`${BASE}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, password, password_confirmation: password }),
      });

      let data;
      try {
        // Read JSON from a clone so rrweb can still clone the original
        const cloned = response.clone();
        data = await cloned.json();
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        return {
          success: false,
          error: 'Invalid response from server. Please try again.'
        };
      }

      if (response.ok && data.success) {
        console.log('Registration successful for user:', data.data.user.email);
        localStorage.setItem('token', data.data.token);
        setUser(data.data.user);
        return { success: true, data: data.data };
      } else {
        const errorMessage = data.message || (response.status === 422 ? 'Validation failed' : 'Registration failed');
        console.log('Registration failed:', errorMessage);
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (error) {
      console.error('Registration failed:', error);
      return {
        success: false,
        error: 'Network error. Please check your connection and try again.'
      };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${BASE}/api/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
    } catch (error) {
      console.error('Logout API call failed:', error);
    }

    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export { AuthContext };
